import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createSolutionGrid,
  updateSolutionGrid,
} from '../../../../utils/httpRequest';
import { useSelector } from 'react-redux';
import {
  fetchSolutionGrid,
  selectCurrentSolutionGridVersions,
  selectIsFetchingSolutionGrid,
  selectIsSolutionGridUpdated,
  selectSolutionGrids,
  selectSolutionGridVersions,
  selectSourceSolutionGrids,
  setInitSolutionGridState,
} from '../../../../slices/solutionGrids';
import { SectionTitle } from '../info/styles';
import { Container, VersionButton } from './styles';
import {
  Badge,
  Button,
  Dropdown,
  Flex,
  Input,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import SolutionGrid, {
  SolutionGridMode,
} from '../../../../components/solutionGrid';
import { useBlocker, useParams } from 'react-router';
import { DownOutlined } from '@ant-design/icons';
import { differenceWith, isEqual, uniqBy } from 'lodash';
import { NewOrExisting } from '../../../../types';
import { useAppDispatch } from '../../../../store';
import VersionInfoModal, { VersionInfoType } from './versionInfoModal';
import { useTheme } from 'styled-components';
import VersionsDrawer from './versionsDrawer';

const TemplateSolutionGrid = () => {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const theme = useTheme();
  const [messageApi, contextHolder] = message.useMessage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVersionInfoModal, setShowVersionInfoModal] = useState(false);
  const [showVersionsDrawer, setShowVersionsDrawer] = useState(false);
  const versions = useSelector(selectSolutionGridVersions);
  const currentVersion = useSelector(selectCurrentSolutionGridVersions);
  const isUpdated = useSelector(selectIsSolutionGridUpdated);
  const isFetchingSolutionGrid = useSelector(selectIsFetchingSolutionGrid);
  const solutionGrids = useSelector(selectSolutionGrids);
  const sourceSolutionGrids = useSelector(selectSourceSolutionGrids);

  const dispatch = useAppDispatch();
  const blocker = useBlocker(isUpdated);

  const handleMenuClick = (e: any) => {
    dispatch(fetchSolutionGrid({ templateID: String(id), versionID: e.key }));
  };

  const menuProps = {
    items: versions?.map((i) => ({
      label: (
        <Flex gap={10}>
          <Badge
            color={
              i.versionID === currentVersion?.versionID ? '#93bc5f' : '#c6c8ca'
            }
          />
          <div>
            {i.versionName}
            <br />
            <Typography.Text type="secondary">
              {i.description || '-'}
            </Typography.Text>
          </div>
        </Flex>
      ),
      key: String(i.versionID),
    })),
    selectedKeys: [String(currentVersion?.versionID)],
    onClick: handleMenuClick,
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      const diffItems = differenceWith(
        solutionGrids,
        sourceSolutionGrids,
        isEqual
      );
      const updatedItems = diffItems.filter(
        (i) => !!sourceSolutionGrids.find((s) => s.gridID === i.gridID)
      );
      const newItems = diffItems.filter(
        (i) => !sourceSolutionGrids.find((s) => s.gridID === i.gridID)
      );
      await Promise.all([
        updatedItems.length && updateSolutionGrid(updatedItems, id),
        newItems.length &&
          createSolutionGrid(
            {
              newOrExisting: NewOrExisting.EXISTING,
              versionID: currentVersion?.versionID,
              solutionGridDtoList: newItems,
            },
            id
          ),
      ]);

      dispatch(fetchSolutionGrid({ templateID: String(id) }));
      messageApi.success('Solution Grid Save Successfully!');
    } catch (err: any) {
      messageApi.error(err?.message || 'Save Failed!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAsNew = async ({ name, description }: VersionInfoType) => {
    try {
      setShowVersionInfoModal(false);
      setIsSubmitting(true);
      const res = await createSolutionGrid(
        {
          newOrExisting: NewOrExisting.NEW,
          versionName: name,
          versionDescription: description,
          solutionGridDtoList: solutionGrids,
        },
        id
      );
      messageApi.success('New Solution Grid Version Created!');
      dispatch(setInitSolutionGridState(res.data));
    } catch (err: any) {
      console.error(err);
      messageApi.error(err?.message || 'Save Failed!');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Flex align="center" style={{ marginBottom: 16, marginTop: 8 }} gap={24}>
        <SectionTitle>Solution Grid</SectionTitle>
        <Flex flex={1} justify="start" align="center">
          {currentVersion && (
            <>
              <Dropdown menu={menuProps} disabled={isFetchingSolutionGrid}>
                <VersionButton>
                  <Badge color="#93bc5f" />
                  <Flex flex={1}>{currentVersion?.versionName}</Flex>

                  <DownOutlined style={{ fontSize: '0.6rem' }} />
                </VersionButton>
              </Dropdown>
              <Button
                ghost
                type="primary"
                style={{ border: 0 }}
                onClick={() => setShowVersionsDrawer(true)}
              >
                {t('edit_versions')}
              </Button>
            </>
          )}
        </Flex>
        {isUpdated && !isFetchingSolutionGrid && (
          <>
            <Button type="primary" onClick={handleSave}>
              {t('save')}
            </Button>
            <Button
              type="primary"
              onClick={() => setShowVersionInfoModal(true)}
            >
              {t('save_as_new_version')}
            </Button>
          </>
        )}
      </Flex>
      <Flex flex={1}>
        <SolutionGrid
          templateId={id}
          mode={SolutionGridMode.UPDATE_RATIO}
        ></SolutionGrid>
      </Flex>
      {isSubmitting && <Spin spinning={isSubmitting} fullscreen />}
      {contextHolder}
      <VersionInfoModal
        open={showVersionInfoModal}
        onClose={() => setShowVersionInfoModal(false)}
        onConfirm={handleSaveAsNew}
      />
      <Modal
        open={blocker.state === 'blocked'}
        title={t('unsaved_changes')}
        okText={t('stay_here')}
        cancelText={t('leave_anyway')}
        onOk={() => {
          if (blocker && blocker.reset) {
            blocker.reset();
          }
        }}
        onCancel={() => {
          if (blocker && blocker.proceed) {
            blocker.proceed();
          }
        }}
      >
        <p style={{ paddingBottom: 20 }}>
          If you leave this page, your changes will be lost. Are you sure?
        </p>
      </Modal>
      <VersionsDrawer
        open={showVersionsDrawer}
        onClose={() => setShowVersionsDrawer(false)}
        templateID={String(id)}
        onConfirm={() => {
          dispatch(fetchSolutionGrid({ templateID: String(id) }));
          setShowVersionsDrawer(false);
        }}
      />
    </Container>
  );
};

export default TemplateSolutionGrid;
